import type { Optional } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import cx from 'classnames'
import { type CSSProperties, type ReactNode, useMemo } from 'react'

import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import {
  type ConfigurationTemplateFunctionValue,
  isSubTemplatedField,
  isTemplateField,
  type SimpleTemplateField,
  type SubTemplateFieldValue,
  type SubTemplateValueSetupField,
  type TemplateField,
  type TemplateVersion,
  ValueSetupRole,
} from '../../types/templateModel'
import {
  type AddDynamicFunctionPlaceholder,
  isAddDynamicFunctionPlaceholder,
} from '../dynamicFunction/types'
import { getSpecialCasedSubTemplatedFieldValue } from '../specialCasedSubTemplatedViewers/helpers'
import {
  isSpecialCasedSubTemplatedValueSetup,
  type SpecialCasedSubTemplatedValueSetup,
} from '../specialCasedSubTemplatedViewers/types'
import { useSpecialSubTemplatedTemplatedConfigurations } from '../specialCasedSubTemplatedViewers/useSpecialSubTemplatedTemplatedConfigurations'
import {
  DEFAULT_STEP_INDENTATION,
  StackableTable,
} from '../stackableTable/StackableTable'
import type { RowActions } from '../stackableTable/types'
import { AddSubtemplatedVariantModal } from './AddSubtemplatedVariantModal'
import { ChildFieldTableCell } from './ChildFieldTableCell'
import {
  type ConfigurationTemplateFunctionValueWithPosition,
  SIMPLIFIED_TEMPLATE_FIELDS_COLUMN_RATIOS,
  TEMPLATE_FIELDS_COLUMN_RATIOS,
} from './fieldsHelpers'
import { ParentFieldTableCell } from './ParentFieldTableCell'
import { SingleClassSubTemplateValueSetupTable } from './SingleClassSubTemplateValueSetupTable'
import { SubTemplateValueSetupTable } from './SubTemplateValueSetupTable'
import { useTemplateFieldsTableContext } from './templateFieldsTableContext'
import { useCategorizeTemplateFields } from './useCategorizeTemplateFields'
import { useChildActions } from './useChildActions'
import { useConvertToStackableType } from './useConvertToStackableType'
import { useEditSubtemplates } from './useEditSubtemplates'
import { useNavigateToCurrentTemplatePath } from './useNavigateToCurrentTemplatePath'
import { useTemplateFieldTableFunctions } from './useTemplateFieldTableFunctions'

export type TemplateFieldTableParent =
  | TemplateVersion
  | SubTemplateValueSetupField
  | SubTemplateFieldValue

export type TemplateFieldChild =
  | SimpleTemplateField
  | SpecialCasedSubTemplatedValueSetup
  | ConfigurationTemplateFunctionValueWithPosition
  | AddDynamicFunctionPlaceholder

type Props = {
  level: number
  parent: TemplateFieldTableParent
  fields?: TemplateField[]
  declaredFunctions?: Optional<ConfigurationTemplateFunctionValue[]>
  dynamicFunctions?: Optional<ConfigurationTemplateFunctionValue[]>
  children?: ReactNode
  isCollapsible: boolean
  onSelect?: (path: string) => void
}

export const TemplateFieldsTable = ({
  fields = [],
  parent,
  level,
  declaredFunctions,
  dynamicFunctions,
  children,
  isCollapsible,
  onSelect,
}: Props) => {
  const translate = useTranslate()
  const { isTableInsideDetailsPanel, selectedSubFieldPath } =
    useTemplateFieldsTableContext()
  const specialConfigurations = useSpecialSubTemplatedTemplatedConfigurations()
  const navigateToPath = useNavigateToCurrentTemplatePath()
  const { simple, composite, specialCased, specialCasedType } =
    useCategorizeTemplateFields(fields)

  const {
    isDraftTemplate,
    isPathCollapsed,
    onToggleTemplatePath,
    detailsPath,
  } = useTemplateContext()
  const {
    preferences: {
      templates: { isShowingNativeView, isShowingSimplifiedView },
    },
  } = useLayoutContext()
  const convertToStackableType = useConvertToStackableType()
  const {
    canAddSubtemplate,
    canRemoveSubTemplate,
    onRemoveParent,
    onAddParent,
    isAddVariantModalOpen,
    onCloseVariantModal,
  } = useEditSubtemplates(parent)
  const isRenderingSpecialCaseView = specialCasedType && !isShowingNativeView
  const isRemoveAllowed = isDraftTemplate && !isRenderingSpecialCaseView
  const allFunctions = useTemplateFieldTableFunctions({
    parent,
    declaredFunctions,
    dynamicFunctions,
  })
  const childActions = useChildActions({
    isRemoveAllowed,
  })

  const nonEmptyColumnHeaders = useMemo(() => {
    if (isShowingSimplifiedView) {
      return [translate('templateViewer.header.value')]
    }

    if (specialCasedType) {
      return (
        specialConfigurations[specialCasedType]?.variableWidthTableColumns ?? []
      ).map(({ title }) => title)
    }

    return [
      translate('templateViewer.header.type'),
      translate('templateViewer.header.required'),
      translate('templateViewer.header.value'),
      translate('templateViewer.header.frameAndValidations'),
    ]
  }, [
    isShowingSimplifiedView,
    specialCasedType,
    specialConfigurations,
    translate,
  ])
  const headers = useMemo(
    () => ['', ...nonEmptyColumnHeaders],
    [nonEmptyColumnHeaders],
  )

  const emptyRowColumnRatios = useMemo(() => {
    if (isShowingSimplifiedView) {
      return SIMPLIFIED_TEMPLATE_FIELDS_COLUMN_RATIOS
    }

    if (specialCasedType) {
      return (
        specialConfigurations[specialCasedType]?.variableWidthTableColumns ?? []
      ).map(({ ratio }) => ratio)
    }

    return TEMPLATE_FIELDS_COLUMN_RATIOS
  }, [isShowingSimplifiedView, specialCasedType, specialConfigurations])

  const allChildEntries = useMemo(
    () => [...simple, ...specialCased, ...allFunctions],
    [allFunctions, simple, specialCased],
  )

  const allTableRows = useMemo(
    () =>
      allChildEntries.map((element) =>
        convertToStackableType({
          data: element,
          headersCount: headers.length,
          isHeaderElement: false,
          specialCasedType,
          isSimplifiedView: !!isShowingSimplifiedView,
          activePath: isTableInsideDetailsPanel
            ? selectedSubFieldPath
            : detailsPath,
        }),
      ),
    [
      allChildEntries,
      convertToStackableType,
      headers.length,
      specialCasedType,
      isShowingSimplifiedView,
      isTableInsideDetailsPanel,
      selectedSubFieldPath,
      detailsPath,
    ],
  )

  const stackableParent = useMemo(
    () =>
      convertToStackableType({
        data: parent,
        headersCount: headers.length,
        isHeaderElement: true,
        specialCasedType,
        isSimplifiedView: !!isShowingSimplifiedView,
        activePath: isTableInsideDetailsPanel
          ? selectedSubFieldPath
          : detailsPath,
      }),
    [
      convertToStackableType,
      parent,
      headers.length,
      specialCasedType,
      isShowingSimplifiedView,
      isTableInsideDetailsPanel,
      selectedSubFieldPath,
      detailsPath,
    ],
  )

  const mainColumnWidthPx = useMemo(() => {
    if (isTableInsideDetailsPanel) {
      return 200
    }

    return specialCased.length ? 300 : undefined
  }, [isTableInsideDetailsPanel, specialCased.length])

  const isParentCollapsed = isPathCollapsed(parent.path)
  const isLeftSpaceRequired = level > 0 || isCollapsible

  const parentActions = useMemo(() => {
    const actions: RowActions<TemplateFieldTableParent> = []

    if (canAddSubtemplate) {
      actions.push({
        actionId: 'addSubtemplate',
        action: onAddParent,
        title: translate('templateViewer.actions.add'),
        node: <IconButton iconName="PlusCircle" />,
      })
    }

    if (canRemoveSubTemplate && !isRenderingSpecialCaseView) {
      actions.push({
        actionId: 'removeSubTemplate',
        action: onRemoveParent,
        title: translate('templateViewer.actions.remove'),
        node: <IconButton iconName="MinusCircle" />,
      })
    }

    return actions
  }, [
    canAddSubtemplate,
    canRemoveSubTemplate,
    isRenderingSpecialCaseView,
    onAddParent,
    onRemoveParent,
    translate,
  ])

  return (
    <div
      className={cx('grid gap-x-2', {
        'grid-cols-[var(--step-indentation)_1fr]': isLeftSpaceRequired,
      })}
      style={
        {
          '--step-indentation': `${DEFAULT_STEP_INDENTATION}px`,
        } as CSSProperties
      }
    >
      {isCollapsible ? (
        <IconButton
          iconName={isParentCollapsed ? 'Plus' : 'Minus'}
          iconSize="sm"
          className={cx('size-min justify-center', {
            'mt-10': level === 0,
            'mt-5': level !== 0,
            invisible: !isCollapsible,
          })}
          onClick={() => onToggleTemplatePath(parent.path)}
        />
      ) : (
        <div />
      )}
      <div>
        {isTemplateField(parent) &&
          isSubTemplatedField(parent) &&
          !!isAddVariantModalOpen && (
            <AddSubtemplatedVariantModal
              forField={parent}
              onClose={onCloseVariantModal}
              isIdentifiable={
                parent.role === ValueSetupRole.IDENTIFIABLE_SUB_TEMPLATE
              }
            />
          )}
        <StackableTable
          indentationStep={level}
          parent={stackableParent}
          data={isParentCollapsed ? [] : allTableRows}
          headers={level === 0 ? headers : []}
          rowClassName={cx(
            'overflow-hidden has-[.shadowDiff]:border-warning-500 dark:has-[.shadowDiff]:border-warning-300 has-[.shadowDiff]:border-5 has-[.shadowDiff]:border-solid',
            isShowingSimplifiedView ? 'min-h-12' : 'h-12',
          )}
          onChildClicked={async (row) => {
            if (isAddDynamicFunctionPlaceholder(row.value)) {
              return
            }

            if (isSpecialCasedSubTemplatedValueSetup(row.value)) {
              await navigateToPath(
                getSpecialCasedSubTemplatedFieldValue(row.value)?.path ?? '',
              )

              return
            }

            if (onSelect) {
              onSelect(row.value.path)
            } else {
              await navigateToPath(row.value.path)
            }
          }}
          onParentClicked={async () => {
            if (onSelect) {
              onSelect(parent.path)
            } else {
              await navigateToPath(parent.path)
            }
          }}
          childCell={ChildFieldTableCell}
          parentCell={ParentFieldTableCell}
          emptyRowColumnRatios={emptyRowColumnRatios}
          parentActions={parentActions}
          childActions={childActions}
          mainColumnWidthPx={mainColumnWidthPx}
          stepSizePx={0}
        >
          {(isParentCollapsed ? [] : composite).map((compositeField) =>
            compositeField.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE ? (
              <SingleClassSubTemplateValueSetupTable
                key={`${level}/single-subtemplate/${compositeField.name}`}
                level={level + 1}
                field={compositeField}
                isCollapsible={isCollapsible}
              />
            ) : (
              <SubTemplateValueSetupTable
                key={`${level}/subtemplate/${compositeField.name}`}
                level={level + 1}
                field={compositeField}
                isCollapsible={isCollapsible}
              />
            ),
          )}
        </StackableTable>
        {!!children && !isParentCollapsed && children}
      </div>
    </div>
  )
}
